import { useState } from 'react'
import { PermissionButton } from '../components/Permissions.js'
import { useProject } from '../components/ProjectLayout.js'
import { CopyButton, Dialog, Empty, ErrorBox, Loading, PageHeader, relative } from '../components/ui.js'
import { useApi, useAsync } from '../lib/useApi.js'

export default function ProjectKeys() {
  const { project } = useProject()
  const api = useApi()
  const keys = useAsync(() => api.listKeys(project.id), [api, project.id])
  const [name, setName] = useState('')
  const [type, setType] = useState<'anon' | 'service'>('anon')
  const [creating, setCreating] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [issued, setIssued] = useState<string | null>(null)
  const [revoking, setRevoking] = useState<{ id: string; name: string } | null>(null)
  return (
    <>
      <PageHeader
        title="API keys"
        description="Project credentials for your application. Organization access tokens are managed separately."
        actions={
          <PermissionButton permission="apikey:create"
            className="btn btn-primary"
            onClick={() => {
              setError(null)
              setCreating(true)
            }}
          >
            Create API key
          </PermissionButton>
        }
      />
      {issued && (
        <div className="notice">
          <strong>Copy this key now. It will not be shown again.</strong>
          <div className="secret">{issued}</div>
          <CopyButton value={issued} />
          <button className="btn btn-small" onClick={() => setIssued(null)}>
            Dismiss
          </button>
        </div>
      )}
      <div className="info-banner">
        Anon keys are for client applications and obey row policies. Service keys bypass policies: keep them
        on your server, never in browser code.
      </div>
      {keys.loading ? (
        <Loading />
      ) : keys.error ? (
        <ErrorBox retry={keys.reload}>{keys.error}</ErrorBox>
      ) : (
        <div className="card">
          {!keys.data?.keys.length ? (
            <Empty>No API keys yet. Create a key to connect your application.</Empty>
          ) : (
            keys.data.keys.map((k) => (
              <div className="row" key={k.id}>
                <div className="row-main">
                  <div className="row-title">
                    {k.name} <span className="pill">{k.type}</span>
                  </div>
                  <div className="row-sub">
                    {k.keyPrefix}… · created {relative(k.createdAt)} ·{' '}
                    {k.lastUsedAt ? `used ${relative(k.lastUsedAt)}` : 'never used'}
                  </div>
                </div>
                {k.revokedAt ? (
                  <span className="pill">Revoked</span>
                ) : (
                  <PermissionButton permission="apikey:revoke"
                    className="btn btn-danger btn-small"
                    onClick={() => {
                      setError(null)
                      setRevoking(k)
                    }}
                  >
                    Revoke…
                  </PermissionButton>
                )}
              </div>
            ))
          )}
        </div>
      )}
      <Dialog open={creating} title="Create API key" busy={busy} onClose={() => setCreating(false)}>
        <form
          onSubmit={async (e) => {
            e.preventDefault()
            if (busy) return
            setBusy(true)
            setError(null)
            try {
              const res = await api.createKey(project.id, { name: name.trim(), type })
              setIssued(res.key.plaintext)
              setName('')
              setCreating(false)
              keys.reload()
            } catch (err) {
              setError(err instanceof Error ? err.message : 'Could not create key.')
            } finally {
              setBusy(false)
            }
          }}
        >
          <div className="field">
            <label htmlFor="key-name">Key name</label>
            <input
              id="key-name"
              required
              maxLength={100}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Website frontend"
            />
          </div>
          <div className="field">
            <label htmlFor="key-type">Access type</label>
            <select
              id="key-type"
              value={type}
              onChange={(e) => setType(e.target.value as 'anon' | 'service')}
            >
              <option value="anon">Anon — client-safe, obeys policies</option>
              <option value="service">Service — server only, bypasses policies</option>
            </select>
          </div>
          {type === 'service' && (
            <div className="notice">
              This grants privileged data access. Never share the key or commit it to source control.
            </div>
          )}
          {error && <ErrorBox>{error}</ErrorBox>}
          <div className="modal-actions">
            <button type="button" className="btn" disabled={busy} onClick={() => setCreating(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" disabled={busy || !name.trim()}>
              {busy ? 'Creating…' : 'Create key'}
            </button>
          </div>
        </form>
      </Dialog>
      <Dialog open={revoking !== null} title="Revoke API key" busy={busy} onClose={() => setRevoking(null)}>
        <p>
          Applications using <strong>{revoking?.name}</strong> will lose access after revocation propagates.
          This cannot be undone.
        </p>
        {error && <ErrorBox>{error}</ErrorBox>}
        <div className="modal-actions">
          <button className="btn" disabled={busy} onClick={() => setRevoking(null)}>
            Cancel
          </button>
          <button
            className="btn btn-danger"
            disabled={busy}
            onClick={async () => {
              if (!revoking || busy) return
              setBusy(true)
              setError(null)
              try {
                await api.revokeKey(revoking.id)
                setRevoking(null)
                keys.reload()
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Could not revoke key.')
              } finally {
                setBusy(false)
              }
            }}
          >
            {busy ? 'Revoking…' : 'Revoke key'}
          </button>
        </div>
      </Dialog>
    </>
  )
}
